import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { TouchableOpacity, useWindowDimensions, FlatList, View, Platform, Keyboard, Image } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { FormikErrors, useFormik } from "formik";
import * as Yup from 'yup';
import Popover from "react-native-popover-view/dist/Popover";

//common components
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";

import CommonService from "../../shared/services/commonService";
//Type Module
import { GetOneCustomerDetailsProps, Name, customerAddressPayloadType, customerAddressPropsType, stateApi, stateNameType } from "./addEditCustomerModule";

import { ZenButton, ZenCheckBox, ZenDropdown, ZenIcon, ZenInput, ZenRadioButton, ZenText, ZenToast } from "zen-core-elements";
import { useAppDispatch } from "../../redux/hooks";
import { setContactInfoDirty } from "../../redux/slices/customerSlice";

//Add Edit Contact Info Props
type AddEditContactInfoProps = {
    isEdit: number,
    customerDetails: GetOneCustomerDetailsProps | undefined,
    countryName: Name[]
}

export interface contactInfoForwardRefProps {
    handleSave: () => customerAddressPropsType[] | null
}

let formikEditData: customerAddressPayloadType[]

const AddEditContactInfo = forwardRef<contactInfoForwardRefProps, AddEditContactInfoProps>(({ isEdit, countryName, customerDetails }, ref) => {
    //variable to store windowsDimentions
    const { width } = useWindowDimensions()
    //variable to store app dispatch and selector state
    const dispatch = useAppDispatch()
    //variable to store useRef values 
    const scrollRef = useRef<any>(null)
    //Variable to handle input container width
    const [inputWidth, setInputWidth] = useState<'50%' | '100%'>('100%')
    //Variable to store formik Initial Values
    let formikInitialValues: customerAddressPayloadType = { id: -1, addressName: "", addressLine1: "", addressLine2: "", country: -1, state: -1, city: "", landmark: "", zipCode: "", countryCode: "", mobileNumber: "", isDefault: false, addressType: "", otherAddressLine: "" }
    //Variable to store Contact formik initial Values
    const [contactFormikInitial, setContactFormikInitial] = useState<customerAddressPayloadType[]>([])
    //Variable to handle collapse header 
    const [collapseHeader, setCollapseHeader] = useState<number[]>([])
    //Varaible to handle current dropdown open state
    const [dropdownOpen, setDropDownOpen] = useState('')
    //Variable to store state name
    const [statesName, setStatesName] = useState<{ [key: number]: stateNameType[] }>({})
    // Variable to store the visibility of the delete popover
    const [confirmationPopover, setConfirmationPopover] = useState<{ visible: boolean, id: number | null, countryId: number }>({ visible: false, id: null, countryId: -1 })
    // Variable to store address type
    const addressType = [{ value: "Home", label: "Home" }, { value: "Office", label: "Office" }, { value: "Others", label: "Others" }]
    //variable to maintain the keyboard open and close
    const [keyboardOpen, setKeyboardOpen] = useState(false)

    useEffect(() => {
        formikEditData = []
    }, [])
    //useEffect triggered  when dimention(width) change
    useEffect(() => {
        if (width > 600) {
            setInputWidth("50%")
        }
        else {
            setInputWidth("100%")
        }
    }, [width])
    //useEffect triggered  when contactFormikInitial change
    useEffect(() => {
        if (contactFormikInitial?.length) {
            try {
                scrollRef.current.scrollToIndex({ index: (contactFormikInitial?.length - 1), animated: true })
            }
            catch {
                console.log("scroll index issue")
            }
        }
    }, [contactFormikInitial])
    //useEffect triggered  when customer details change
    useEffect(() => {
        if (customerDetails != null) {
            const countrySet = getContactInfo()
            countrySet?.forEach(async (value) => {
                let country: string = countryName?.filter((name: Name) => (name?.formikvalue?.id == value))[0]?.label
                await getStateName(country, value)
            })
        }
    }, [customerDetails])

    //Method for getting contact info formik values
    const getContactInfo = () => {
        const allCountryNames: Set<number> = new Set()
        if (customerDetails?.addresses?.length) {
            let data = []
            for (let i = 0; i < customerDetails?.addresses?.length; i++) {
                let values: customerAddressPayloadType = customerDetails?.addresses[i]
                if (!allCountryNames?.has(values?.country)) {
                    allCountryNames?.add(values?.country)
                }
                data.push({
                    id: values?.id,
                    addressName: values?.addressName || "",
                    addressLine1: values?.addressLine1 || "",
                    addressLine2: values?.addressLine2 || "",
                    country: values?.country || -1,
                    state: values?.state || -1,
                    city: values?.city || "",
                    landmark: values?.landmark || "",
                    zipCode: values?.zipCode || "",
                    countryCode: values?.countryCode ? ("+" + values?.countryCode) : "",
                    mobileNumber: values?.mobileNumber || "",
                    isDefault: false,
                    addressType: values?.addressType || "",
                    otherAddressLine: values?.otherAddressLine || ""
                })
            }
            setCollapseHeader(Array.from(Array(customerDetails?.addresses?.length).keys(), num => num))
            formikEditData = data
            setContactFormikInitial(data)
        }
        else {
            setContactFormikInitial([])
        }
        return allCountryNames
    }
    //Method for contactformik validation
    const contactValidation = Yup.object().shape({
        formikData: Yup.array().of(
            Yup.object().shape({
                addressName: Yup.string().label("addressName").required("Full name is required")
                    .max(30, "Maxmimum length of Full name exceed")
                    .matches(/^[a-zA-Z ]+$/, 'Full Name accepts characters only'),
                addressLine1: Yup.string().label("addressLine1").required("Address1 is required"),
                country: Yup.string()
                    // .label("Country")
                    .matches(/^[1-9]\d*$/, "Country is required")
                    .required("Country is required"),
                state: Yup.string()
                    // .label("State")
                    .matches(/^[1-9]\d*$/, "State is required")
                    .required("State is required"),
                city: Yup.string().label("city").required("City is required"),
                zipCode: Yup.string().label("zipCode").required("Zipcode is required").max(6, "Zip code must contain 6 digits"),
                countryCode: Yup.string().label("countryCode").required("country code is required"),
                mobileNumber: Yup.string().label('mobileNumber').required("Contact number is required")
                    .length(10, "Contact number must contain 10 digits and first number cannot be 0 or 1")
                    .matches(/^[2-9]\d{9}$/, "Contact number is invalid"),
            })
        )
    })
    //Contact formik
    const contactFormik = useFormik({
        initialValues: { formikData: contactFormikInitial },
        validationSchema: contactValidation,
        enableReinitialize: true,
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: () => {
            console.log("submit contact formik")
        }
    })
    //Contact formik errors
    const contactFormikErrors = contactFormik?.errors?.formikData as FormikErrors<customerAddressPayloadType[]>;

    //Variable to store contact details props
    const generateContactProps = () => {
        let data: customerAddressPropsType[] = []
        contactFormik?.values?.formikData?.map((item, index: number) => {
            data?.push({
                "id": (item?.id && item?.id != -1) ? item?.id : null,
                "name": item?.addressName,
                "addressLine1": item?.addressLine1,
                "addressLine2": item?.addressLine2 ? item?.addressLine2 : null,
                "country": countryName?.filter((value: Name) => value.value == contactFormik?.values.formikData[index]?.['country'])[0]?.formikvalue,
                "state": statesName[contactFormik?.values.formikData[index]?.['country']].filter((value: stateNameType) => value.value == contactFormik?.values.formikData[index]?.['state'])[0]?.formikvalue,
                "city": item?.city,
                "landmark": item?.landmark ? item?.landmark : null,
                "zipCode": item?.zipCode,
                "ContactCountryCode": item?.countryCode,
                "contactNumber": item?.mobileNumber,
                "isDefault": item?.isDefault,
                "addressType": item?.addressType ? item?.addressType : null,
                "otherAddressLine": item?.otherAddressLine ? item?.otherAddressLine : null,
            })
        })
        return data
    }

    useEffect(() => {
        if (contactFormik?.dirty) {
            dispatch(setContactInfoDirty(true))
        }
        else {
            if (isEdit) {
                if (!compareArrays(formikEditData ? formikEditData : [], contactFormikInitial)) {
                    dispatch(setContactInfoDirty(true))
                }
                else {
                    dispatch(setContactInfoDirty(false))
                }
            } else {
                if (!contactFormikInitial?.length) {
                    dispatch(setContactInfoDirty(false))
                }
                else if (!compareArrays([formikInitialValues], contactFormikInitial)) {
                    dispatch(setContactInfoDirty(true))
                }
                else {
                    dispatch(setContactInfoDirty(false))
                }
            }
        }
    }, [contactFormik?.dirty, contactFormikInitial])
    //useImperativeHandle triggered  when save button pressed
    useImperativeHandle(ref, () => ({
        handleSave: () => {
            contactFormik?.handleSubmit()
            if (!isEdit && contactFormikInitial?.length == 0) {
                return generateContactProps()
            }
            else {
                if (contactFormik?.isValid && contactFormik?.values?.formikData && contactFormik?.values?.formikData[contactFormikInitial?.length - 1]?.addressName != '') {
                    return generateContactProps()
                }
                else {
                    return null
                }
            }
        }
    }))
    //UseEffect for handle keyboard
    useEffect(() => {
        Keyboard.addListener("keyboardDidShow", () => {
            setKeyboardOpen(true);
        });
        Keyboard.addListener("keyboardDidHide", () => {
            setKeyboardOpen(false);
        });
    }, [])

    //Method for handle collapse header state
    const collapseFunction = (index: number) => {
        setCollapseHeader((prev: number[]) => {
            let data = [...prev]
            let findIndex = data?.some((value) => value == index)
            if (findIndex) {
                data[index] = -1
            }
            else {
                data[index] = index
            }
            return data
        })
    }
    //Method for handle dropdown open/close state
    const openDropdown = (formikName: string) => {
        if (formikName == dropdownOpen) {
            setDropDownOpen('')
        }
        else {
            setDropDownOpen(formikName)
        }
    }
    const findStateName = (countryId: number) => {
        return Object?.keys(statesName)?.find((item) => countryId.toString() === item)
    }
    //Method for getting state name
    const getStateName = async (countryValue: string, countryId: number) => {
        await CommonService?.getStateName(countryValue).then((response: { states: stateApi[] }) => {
            if (response && response?.states) {
                let states: stateNameType[] = []
                response?.states?.map((value) => {
                    let formikvalue = { ...value, "countryId": countryId }
                    states?.push({ label: value?.name, value: value?.id, formikvalue })
                })
                setStatesName((prev) => ({
                    ...prev,
                    [countryId]: states
                }))
            }
        }).catch((error: any) => {
            console?.log("Get state error", error)
        })
    }
    //Method for comapare two arrays
    const compareArrays = (arr1: customerAddressPayloadType[], arr2: customerAddressPayloadType[]) => {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let value of arr1) {
            const matchingObject = arr2.find((obj) => {
                return Object.keys(obj).every(key => obj[key as keyof customerAddressPayloadType] === arr1[0][key as keyof customerAddressPayloadType]);
            });
            if (!matchingObject) {
                return false;
            }
        }
        return true;
    }
    const removeStateName = (countryId: number) => {
        let countryExistCount = contactFormik?.values?.formikData?.filter((item) => item?.country === countryId)
        if (countryExistCount?.length == 1) {
            setStatesName((prev) => {
                const { [countryId]: _, ...remaining } = prev
                return remaining
            })
        }
    }
    //method for popover confirmation 
    const confirmation = (item: { id: number }) => {
        if (item?.id == 2) {
            setContactFormikInitial((prev) => {
                let data = [...contactFormik?.values?.formikData]
                return data?.filter((u, subindex) => subindex != confirmationPopover?.id)

            })
            setCollapseHeader((prev: number[]) => {
                let data = [...prev]
                data?.map((_, index) => {
                    if (confirmationPopover?.id != null) {
                        if (index > confirmationPopover?.id) {
                            if (data[index] != -1)
                                data[index] = data[index] - 1
                        }
                    }
                })
                return data?.filter((_, index) => index != confirmationPopover?.id)
            })
            removeStateName(confirmationPopover?.countryId)
            setConfirmationPopover({ visible: false, id: null, countryId: -1 })
            ZenToast({ type: "success", message: `Address ${confirmationPopover?.id ? (confirmationPopover?.id + 1) : 1} was removed successfully.` })
        }
    }

    //Medthod for flatList renderItem
    const renderItem = ({ item, index }: { item: customerAddressPayloadType, index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }: { theme: Theme }) => (
                    <View key={index} style={{ marginTop: index == 0 ? 15 : 5, paddingTop: 0, marginHorizontal: width > 600 ? 15 : 10, paddingHorizontal: 0, overflow: "hidden", paddingBottom: 0, backgroundColor: "transparent", marginBottom: 10, borderRadius: 10 }}>
                        <TouchableOpacity style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: '#fbfbfb', padding: 10, borderRadius: index == collapseHeader[index] ? 0 : 10 }} onPress={() => { collapseFunction(index) }}>
                            <View style={{ flexDirection: "row", alignItems: "center", marginLeft: 5 }}>
                                <ZenIcon type="material-community" name={index == collapseHeader[index] ? "chevron-up" : "chevron-down"} size={26} color={theme?.primary} />
                                <ZenText content={`Address ${index + 1}`} size={width > 600 ? "medium" : theme?.smallerFontSize} color={theme.primary} bold style={{ marginHorizontal: 5, }} />
                            </View>
                            <TouchableOpacity style={{ marginHorizontal: 10 }} onPress={() => { setConfirmationPopover({ visible: true, id: index, countryId: contactFormik?.values.formikData[index]?.['country'] }) }}>
                                <ZenIcon type="material-community" name="delete" size={width > 600 ? 22 : 18} color={"red"} />
                            </TouchableOpacity>
                        </TouchableOpacity>
                        <View style={[theme?.addEditCustomerStyle?.generalSectionView, { justifyContent: "space-between", paddingHorizontal: width > 600 ? 10 : 5, display: index == collapseHeader[index] ? "flex" : "none", backgroundColor: theme?.light, marginVertical: 0, paddingVertical: 10 }]}>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['addressName']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].addressName`, text)}
                                    label={"Full Name*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].addressName`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].addressName`, contactFormik?.values.formikData[index]?.['addressName']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.addressName && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressName'] ? contactFormikErrors?.[index]?.addressName : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.addressName && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressName'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['addressLine1']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].addressLine1`, text)}
                                    label={"Address 1*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={contactFormik?.handleBlur(`formikData[${index}].addressLine1`)}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.addressLine1 && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressLine1'] ? contactFormikErrors?.[index]?.addressLine1 : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.addressLine1 && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressLine1'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['addressLine2']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].addressLine2`, text)}
                                    label={"Address 2"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].addressLine2`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].addressLine2`, contactFormik?.values.formikData[index]?.['addressLine2']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.addressLine2 && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressLine2'] ? contactFormikErrors?.[index]?.['addressLine2'] : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.addressLine2 && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['addressLine2'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={[{ width: inputWidth }, Platform.OS == "ios" && { zIndex: 10 }]}>
                                <View style={[{ marginHorizontal: 15, marginBottom: contactFormikErrors?.[index]?.['country'] && contactFormik?.touched?.formikData?.[index]?.["country"] && contactFormik?.values.formikData[index]?.["country"] == -1 ? 0 : 25 }, Platform.OS == "ios" && { zIndex: 10 }]}>
                                    <ZenDropdown
                                        title="Country*"
                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                        items={countryName}
                                        open={'country' + index == dropdownOpen}
                                        setOpen={() => { }}
                                        onPress={() => {
                                            openDropdown("country" + index)
                                        }}
                                        setValue={() => { }}
                                        onOpen={() => {
                                            contactFormik?.setFieldTouched(`formikData[${index}].country`, true)
                                        }}
                                        value={contactFormik?.values.formikData[index]?.['country'] != -1 ? contactFormik?.values.formikData[index]?.['country'] : ""}
                                        onSelectItem={(value) => {
                                            let values = value as Name
                                            let oldCountryId = contactFormik?.values.formikData[index]?.['country']
                                            contactFormik?.setFieldValue(`formikData[${index}].country`, values?.value)
                                            if (findStateName(values?.value as number) === undefined) {
                                                getStateName(values?.label, values?.value as number)
                                            }
                                            if (contactFormik?.values?.formikData?.[index]?.state != -1) {
                                                contactFormik?.setFieldTouched(`formikData[${index}].state`, true)
                                            }
                                            contactFormik?.setFieldValue(`formikData[${index}].state`, -1)
                                            contactFormik?.setFieldValue(`formikData[${index}].countryCode`, values?.countryCode)

                                            removeStateName(oldCountryId)
                                            setDropDownOpen('')
                                        }
                                        }
                                        multiple={false}
                                        searchable={true}
                                        placeholder={"Select a country"}
                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                    />
                                </View>
                                {contactFormikErrors?.[index]?.country && contactFormik?.touched?.formikData?.[index]?.["country"] && contactFormik?.values.formikData?.[index]?.["country"] == -1 &&
                                    <ZenText content={contactFormikErrors?.[index]?.country} size={theme?.errorFontSize} color={theme?.red} style={[theme?.modifierSetStyle?.dropdownErrorStyle, { paddingVertical: contactFormikErrors?.[index]?.country && contactFormik?.touched?.formikData?.[index]?.["country"] && contactFormik?.values.formikData[index]?.["country"] == -1 ? 15 : 0 }]} />}
                            </View>
                            <View style={[{ width: inputWidth }, Platform.OS == "ios" && { zIndex: 9 }]}>
                                <View style={[{ marginHorizontal: 15, marginBottom: contactFormikErrors?.[index]?.state && contactFormik?.touched?.formikData?.[index]?.["state"] && contactFormik?.values.formikData[index]?.["state"] == -1 ? 0 : 25 }, Platform.OS == "ios" && { zIndex: 10 }]}>
                                    <ZenDropdown
                                        title={"State*"}
                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                        items={statesName[contactFormik?.values.formikData[index]?.['country']] || []}
                                        open={'state' + index == dropdownOpen}
                                        setOpen={() => { }}
                                        onPress={() => {
                                            if (statesName[contactFormik?.values.formikData[index]?.['country']]) {
                                                openDropdown("state" + index)
                                            }
                                        }}
                                        setValue={() => { }}
                                        onOpen={() => {
                                            contactFormik?.setFieldTouched(`formikData[${index}].state`, true)
                                        }}
                                        value={contactFormik?.values.formikData[index]?.['state'] != -1 ? contactFormik?.values.formikData[index]?.['state'] : ''}
                                        onSelectItem={async (values) => {
                                            contactFormik?.setFieldTouched(`formikData[${index}].state`, true)
                                            contactFormik?.setFieldValue(`formikData[${index}].state`, values?.value)
                                            setDropDownOpen('')
                                        }}
                                        multiple={false}
                                        searchable={true}
                                        // style={[theme?.addEditCustomerStyle?.dropdownStyle, (Platform?.OS == "android" && ('state' + index != dropdownOpen)) && { zIndex: 0 }]}
                                        placeholder={"Select a state"}
                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                    />
                                </View>
                                {contactFormikErrors?.[index]?.state && contactFormik?.touched?.formikData?.[index]?.["state"] && contactFormik?.values.formikData[index]?.["state"] == -1 &&
                                    <ZenText content={contactFormikErrors?.[index]?.state} size={theme?.errorFontSize} color={theme?.red} style={[theme?.modifierSetStyle?.dropdownErrorStyle, { paddingVertical: contactFormikErrors?.[index]?.state && contactFormik?.touched?.formikData?.[index]?.["state"] && contactFormik?.values.formikData[index]?.["state"] == -1 ? 15 : 0 }]} />}
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['city']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].city`, text)}
                                    label={"City*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].city`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].city`, contactFormik?.values.formikData[index]?.['city']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.city && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['city'] ? contactFormikErrors?.[index]?.city : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.city && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['city'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['landmark']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].landmark`, text)}
                                    label={"Landmark"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].landmark`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].landmark`, contactFormik?.values.formikData[index]?.['landmark']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.landmark && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['landmark'] ? contactFormikErrors?.[index]?.landmark : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.landmark && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['landmark'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    keyboardType="numeric"
                                    value={contactFormik?.values.formikData[index]?.['zipCode']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].zipCode`, text)}
                                    label={"Zipcode*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].zipCode`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].zipCode`, contactFormik?.values.formikData[index]?.['zipCode']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.zipCode && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.zipCode ? contactFormikErrors?.[index]?.zipCode : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.zipCode && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['zipCode'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    editable={false}
                                    value={contactFormik?.values.formikData[index]?.['countryCode']}
                                    onChangeText={(text) => { }}
                                    label={"Country code*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.countryCode && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['countryCode'] ? contactFormikErrors?.[index]?.countryCode : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.countryCode && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['countryCode'] ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenInput
                                    keyboardType="phone-pad"
                                    value={contactFormik?.values.formikData[index]?.['mobileNumber']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].mobileNumber`, text)}
                                    label={"Contact number*"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].mobileNumber`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].mobileNumber`, contactFormik?.values.formikData[index]?.['mobileNumber']?.trim()) }}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.mobileNumber && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.['mobileNumber'] ? contactFormikErrors?.[index]?.mobileNumber : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.mobileNumber && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.mobileNumber ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View>
                            <View style={[theme?.addEditCustomerStyle?.wholesaleView, { paddingBottom: 25, paddingTop: 10, width: inputWidth }]}>
                                <ZenCheckBox
                                    title={'Default Address'}
                                    titleStyle={{ fontFamily: theme?.medium, fontSize: theme?.smallerFontSize, color: theme?.lightDark }}
                                    checked={contactFormik?.values?.formikData[index]?.['isDefault']}
                                    checkedColor={theme?.primary}
                                    containerStyle={theme?.addEditCustomerStyle?.checkBoxContainerStyle}
                                    onPress={() => {
                                        contactFormik?.setFieldValue(`formikData[${index}].isDefault`, !contactFormik?.values?.formikData[index]?.['isDefault'])
                                    }}
                                />
                            </View>
                            <View style={{ width: inputWidth }}>
                                <ZenText content={"Type of address"} color={theme?.labelColor} style={theme?.addEditCustomerStyle?.dropdownLabelStyle} />
                                <View style={{ paddingBottom: 15 }}>
                                    <ZenRadioButton
                                        initialSelected={contactFormik?.values?.formikData[index]?.['addressType']}
                                        options={addressType}
                                        titleStyle={{ color: theme?.lightDark, fontSize: theme?.smallerFontSize }}
                                        containerStyle={{ flexDirection: "row", columnGap: 20, justifyContent: "space-between" }}
                                        onSelect={(value) => {
                                            if (value != 'Others' && contactFormik?.values.formikData?.[index]?.['otherAddressLine'] != '') {
                                                contactFormik?.setFieldValue(`formikData[${index}].otherAddressLine`, '')
                                            }
                                            contactFormik?.setFieldValue(`formikData[${index}].addressType`, value)
                                        }}
                                    />
                                </View>
                            </View>
                            {contactFormik?.values?.formikData[index]?.['addressType'] == 'Others' ? <View style={{ width: inputWidth }}>
                                <ZenInput
                                    value={contactFormik?.values.formikData[index]?.['otherAddressLine']}
                                    onChangeText={(text) => contactFormik?.setFieldValue(`formikData[${index}].otherAddressLine`, text)}
                                    onBlur={() => {
                                        contactFormik?.setFieldTouched(`formikData[${index}].otherAddressLine`, true)
                                    }}
                                    onEndEditing={() => { contactFormik.setFieldValue(`formikData[${index}].otherAddressLine`, contactFormik?.values.formikData[index]?.['otherAddressLine']?.trim()) }}
                                    label={"Enter Address"}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                    errorMessage={contactFormikErrors?.[index]?.otherAddressLine && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.otherAddressLine ? contactFormikErrors?.[index]?.otherAddressLine : ''}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: contactFormikErrors?.[index]?.otherAddressLine && !contactFormik?.isValid && contactFormik?.touched?.formikData?.[index]?.otherAddressLine ? 10 : 0 }}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                />
                            </View> : null}

                        </View>
                    </View>
                )}
            </ThemeConsumer>
        )
    }
    //Medthod for flatList listEmptyComponent
    const listEmptyComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }: { theme: Theme }) => (
                    <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                        <Image source={require('../../assets/down.png')} style={{ width: 100, height: 100 }} resizeMode="contain" />
                        <ZenText content="No contact information is associated with this customer!" numberOfLines={2} style={{ textAlign: "center", paddingTop: 5 }} size={theme?.errorFontSize} color={theme?.dark} />
                        <ZenButton
                            title={'Add Contact Information'}
                            onPress={() => {
                                setContactFormikInitial((prev) => {
                                    let data = [...contactFormik?.values?.formikData]
                                    data?.push(formikInitialValues)
                                    return data
                                });
                                setDropDownOpen('')
                                setCollapseHeader([0])
                            }}
                            buttonStyle={{ backgroundColor: theme?.primary }}
                            containerStyle={{ marginVertical: 10, borderRadius: 5, }}
                        />
                    </View>
                )}
            </ThemeConsumer>
        )
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <View style={{ flex: 1 }} onStartShouldSetResponder={() => { setDropDownOpen(''); return false }}>
                    <FlatList
                        data={contactFormikInitial ? contactFormikInitial : []}
                        keyExtractor={(item, index) => index.toString()}
                        ref={scrollRef}
                        renderItem={renderItem}
                        ListEmptyComponent={listEmptyComponent}
                        contentContainerStyle={{ flex: contactFormikInitial?.length ? undefined : 1, paddingBottom: Platform.OS === 'ios' && keyboardOpen ? 350 : 0 }}
                        onScrollToIndexFailed={info => {
                            const wait = new Promise(resolve => setTimeout(resolve as () => void, 500));
                            wait.then(() => {
                                scrollRef.current?.scrollToIndex({ index: info.index, animated: true });
                            })
                        }}
                        ListFooterComponent={contactFormikInitial?.length ? () => {
                            return (
                                <View style={{ marginTop: 10, marginBottom: 20 }}>
                                    <ZenButton
                                        title="Add Address"
                                        buttonStyle={{ borderRadius: 10, backgroundColor: theme?.primary, paddingHorizontal: 15 }}
                                        containerStyle={{ alignItems: "center" }}
                                        onPress={() => {
                                            if (contactFormik?.isValid && contactFormik?.values?.formikData && contactFormik?.values?.formikData[contactFormikInitial?.length - 1]?.addressName) {
                                                setContactFormikInitial((prev) => {
                                                    let data = [...contactFormik?.values?.formikData]
                                                    data?.push(formikInitialValues)
                                                    return data
                                                })
                                                setCollapseHeader((prev: number[]) => {
                                                    let data = [...prev]
                                                    data?.map((item, index: number) => { data[index] = -1 })
                                                    data?.push(contactFormikInitial?.length)
                                                    return data
                                                })
                                            }

                                            else {
                                                scrollRef.current.scrollToIndex({ index: (contactFormikInitial?.length - 1), animated: true })
                                                contactFormik?.handleSubmit()
                                                ZenToast({ type: "info", message: "Please fill out the previous address details!" })
                                            }

                                        }}
                                    />
                                </View>
                            )
                        } : null}
                    />
                    {/* For delete confirmation */}
                    <Popover isVisible={confirmationPopover?.visible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }} onRequestClose={() => { setConfirmationPopover({ visible: false, id: null, countryId: -1 }) }}>
                        <CommonDialogBox type="DELETE" replaceValue="address" confirmation={confirmation} />
                    </Popover>
                </View>
            )}
        </ThemeConsumer>
    )
})

export default React?.memo(AddEditContactInfo)